// ChatPanel — the slide-out right-hand panel embedded in AppShell
// (docs/plans/2026-09-30-runners/07-chat.md §2.8): a conversation list plus
// the active conversation, so the page underneath (and its `pageContext`,
// e.g. the project you're looking at) stays visible while you talk to the
// assistant. Mobile and the full-bleed workflow editor lay it over the page
// instead — see app-shell.tsx.

import { useState } from 'react'
import type { ChatConversation } from '@goblic/shared'
import { ConversationList } from './conversation-list'
import { ConversationView } from './conversation-view'
import { NewConversationForm } from './new-conversation-form'

export function ChatPanel({
  onClose,
  pageContext,
}: {
  onClose: () => void
  pageContext?: Record<string, unknown>
}) {
  const [activeId, setActiveId] = useState<string | undefined>(undefined)
  const [showList, setShowList] = useState(true)
  const [newOpen, setNewOpen] = useState(false)

  function select(conversation: ChatConversation): void {
    setActiveId(conversation.id)
    setShowList(false)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-line px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          {!showList ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setShowList(true)}
              aria-label="Back to conversations"
            >
              ← Conversations
            </button>
          ) : (
            <p className="font-display text-md font-semibold text-ink">
              Assistant
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={onClose}
          aria-label="Close assistant panel"
        >
          Close
        </button>
      </div>

      <div className="min-h-0 flex-1">
        {showList || !activeId ? (
          <ConversationList
            activeId={activeId}
            onSelect={select}
            onNew={() => setNewOpen(true)}
          />
        ) : (
          <ConversationView
            conversationId={activeId}
            pageContext={pageContext}
            onDeleted={() => {
              setActiveId(undefined)
              setShowList(true)
            }}
          />
        )}
      </div>

      <NewConversationForm
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(conversation) => {
          setNewOpen(false)
          select(conversation)
        }}
      />
    </div>
  )
}
